import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  Chip,
  Avatar,
  Divider,
  Stack,
  Collapse,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Restaurant,
  ExpandMore,
  ExpandLess,
  Edit,
  TrendingUp,
  Inventory2,
  LocalDining,
  CheckCircle,
  Warning,
  Search,
  FilterList,
  ViewList,
  ViewModule,
  Category,
  Refresh
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Import services
import inventoryService from '../../services/inventoryService';
import ingredientsService from '../../services/ingredientsService';

// Import components
import RecipeForm from './RecipeForm';

const MotionCard = motion(Card);

const RecipeSetupTab = ({ 
  onSuccess = () => {}, 
  onError = () => {} 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // FIXED: State management for all data
  const [loading, setLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [recipeData, setRecipeData] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccessMessage] = useState(null);

  // FIXED: Safe formatting function with null checks
  const safeFormat = useCallback((value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00';
    }
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '0.00' : numValue.toFixed(decimals);
  }, []);

  // FIXED: Smart quantity formatting - whole numbers as whole, decimals as-is
  const formatQuantity = useCallback((value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue === 0) return '';
    if (Number.isInteger(numValue)) return numValue.toString();
    return numValue.toString();
  }, []);

  // FIXED: Updated card gradients using theme colors
  const getCardStyling = (category, hasRecipe) => {
    if (!hasRecipe) {
      return {
        background: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
      };
    }
    
    switch (category) {
      case 'raw_materials':
        return {
          background: `linear-gradient(135deg, ${theme.palette.info.main}15 0%, ${theme.palette.info.dark}20 100%)`,
          borderColor: theme.palette.info.main,
          boxShadow: `0 12px 24px ${theme.palette.info.main}25`
        };
      case 'premade_items':
        return {
          background: `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.dark}20 100%)`,
          borderColor: theme.palette.success.main,
          boxShadow: `0 12px 24px ${theme.palette.success.main}25`
        };
      case 'mixed':
        return {
          background: `linear-gradient(135deg, ${theme.palette.warning.main}10 0%, ${theme.palette.warning.dark}15 100%)`,
          borderColor: theme.palette.warning.main,
          boxShadow: `0 12px 24px ${theme.palette.warning.main}25`
        };
      default:
        return {
          background: `linear-gradient(135deg, ${theme.palette.success.main}10 0%, ${theme.palette.success.light}15 100%)`,
          borderColor: theme.palette.success.main,
          boxShadow: `0 12px 24px ${theme.palette.success.main}25`
        };
    }
  };

  const [recipeFormOpen, setRecipeFormOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  // FIXED: Comprehensive data loading function
  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Loading all recipe setup data...');
      
      // Load menu items, ingredients, and recipes in parallel
      const [menuResponse, ingredientsResponse, recipeItemsResponse] = await Promise.allSettled([
        inventoryService.getStock(), // Get menu items with stock data
        ingredientsService.getIngredients(), // Get ingredients
        inventoryService.getRecipeItems() // Get recipe items with ingredients
      ]);

      // Process menu items
      let menuItemsData = [];
      if (menuResponse.status === 'fulfilled' && menuResponse.value?.success) {
        menuItemsData = menuResponse.value.data || [];
        console.log('✅ Menu items loaded:', menuItemsData.length);
      } else {
        console.warn('⚠️ Failed to load menu items');
      }

      // Process ingredients  
      let ingredientsData = [];
      if (ingredientsResponse.status === 'fulfilled' && ingredientsResponse.value?.success) {
        ingredientsData = ingredientsResponse.value.data || [];
        console.log('✅ Ingredients loaded:', ingredientsData.length);
      } else {
        console.warn('⚠️ Failed to load ingredients');
      }

      // Process recipe data
      let recipeItemsData = [];
      if (recipeItemsResponse.status === 'fulfilled' && recipeItemsResponse.value?.success) {
        recipeItemsData = recipeItemsResponse.value.data || [];
        console.log('✅ Recipe items loaded:', recipeItemsData.length);
      } else {
        console.warn('⚠️ Failed to load recipe items');
      }

      // Update state
      setMenuItems(menuItemsData);
      setIngredients(ingredientsData);
      setRecipeData(recipeItemsData);

      console.log('✅ All data loaded successfully');
      
    } catch (error) {
      console.error('❌ Error loading recipe setup data:', error);
      setError(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // FIXED: Get recipe category using improved logic
  const getRecipeCategory = useCallback((menuItem, recipe) => {
    // Check ingredient types if recipe exists
    if (recipe && recipe.ingredients && ingredients.length > 0) {
      const usedIngredientTypes = recipe.ingredients
        .map(recipeIng => ingredients.find(ing => ing.ingredient_id === recipeIng.ingredient_id))
        .filter(ing => ing && ing.type)
        .map(ing => ing.type);
      
      const hasRaw = usedIngredientTypes.includes('raw');
      const hasPremade = usedIngredientTypes.includes('premade');
      
      if (hasRaw && hasPremade) return 'mixed';
      else if (hasRaw) return 'raw_materials';  
      else if (hasPremade) return 'premade_items';
    }
    
    return menuItem?.recipe_category || 'uncategorized';
  }, [ingredients]);

  // Get category label and color - FIXED: Using database category names
  const getCategoryInfo = useCallback((category) => {
    switch (category) {
      case 'raw_materials':
        return {
          label: t('inventory.filters.raw'),
          color: 'info',
          bgColor: theme.palette.info.lighter || 'rgba(33, 150, 243, 0.1)'
        };
      case 'premade_items':
        return {
          label: t('inventory.filters.premade'),
          color: 'success',
          bgColor: theme.palette.success.lighter || 'rgba(76, 175, 80, 0.1)'
        };
      case 'mixed':
        return {
          label: 'Mixed Recipe',
          color: 'warning',
          bgColor: theme.palette.warning.lighter || 'rgba(255, 193, 7, 0.1)'
        };
      case 'uncategorized':
      default:
        return {
          label: t('inventory.filters.uncategorized'),
          color: 'default',
          bgColor: theme.palette.grey[100]
        };
    }
  }, [t, theme]);

  // FIXED: Enhanced menu items with proper recipe mapping
  const enhancedMenuItems = useMemo(() => {
    return menuItems.map(menuItem => {
      // Find recipe data for this menu item
      const recipe = recipeData.find(r => r.item_id === menuItem.item_id);
      const category = getRecipeCategory(menuItem, recipe);
      
      return {
        ...menuItem,
        recipe,
        category,
        categoryInfo: getCategoryInfo(category),
        hasRecipe: !!recipe
      };
    });
  }, [menuItems, recipeData, getRecipeCategory, getCategoryInfo]);

  // Filter and search logic
  const filteredMenuItems = useMemo(() => {
    let filtered = enhancedMenuItems;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(term) ||
        (item.description && item.description.toLowerCase().includes(term)) ||
        item.categoryInfo.label.toLowerCase().includes(term)
      );
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      if (categoryFilter === 'raw') {
        filtered = filtered.filter(item => item.category === 'raw_materials');
      } else if (categoryFilter === 'premade') {
        filtered = filtered.filter(item => item.category === 'premade_items');
      } else {
        filtered = filtered.filter(item => item.category === categoryFilter);
      }
    }

    // Sort by category (uncategorized first, then raw, then premade, then mixed)
    const categoryOrder = { 'uncategorized': 0, 'raw_materials': 1, 'premade_items': 2, 'mixed': 3 };
    filtered.sort((a, b) => {
      const orderA = categoryOrder[a.category] || 999;
      const orderB = categoryOrder[b.category] || 999;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If same category, sort by name
      return a.name.localeCompare(b.name);
    });

    return filtered;
  }, [enhancedMenuItems, searchTerm, categoryFilter]);

  // Category counts for filter options
  const categoryCounts = useMemo(() => {
    const counts = enhancedMenuItems.reduce((acc, item) => {
      let filterCategory = item.category;
      if (item.category === 'raw_materials') filterCategory = 'raw';
      if (item.category === 'premade_items') filterCategory = 'premade';
      
      acc[filterCategory] = (acc[filterCategory] || 0) + 1;
      return acc;
    }, {});
    
    return {
      all: enhancedMenuItems.length,
      uncategorized: counts.uncategorized || 0,
      raw: counts.raw || 0,
      premade: counts.premade || 0,
      mixed: counts.mixed || 0
    };
  }, [enhancedMenuItems]);

  // Toggle card expansion
  const handleCardExpand = (itemId) => {
    setExpandedCards(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Handle create recipe
  const handleCreateRecipe = (menuItem) => {
    setSelectedMenuItem(menuItem);
    setSelectedRecipe(null);
    setRecipeFormOpen(true);
  };

  // FIXED: Handle edit recipe - Load recipe with ingredients
  const handleEditRecipe = async (menuItem, recipe) => {
  setSelectedMenuItem(menuItem);
  setLoading(true);
  setError(null);

  try {
    console.log('🔄 Loading full recipe details for item:', menuItem.item_id);
    
    // Try to get detailed recipe with ingredients, with fallback
    let fullRecipe = recipe; // Start with the recipe data we already have
    
    try {
      const response = await inventoryService.getRecipeWithIngredients(menuItem.item_id);
      if (response.success && response.data) {
        console.log('✅ Full recipe loaded:', response.data);
        fullRecipe = response.data;
      }
    } catch (detailError) {
      console.warn('⚠️ Failed to fetch detailed recipe, using basic data:', detailError);
      // Continue with the recipe data we already have
    }
    
    setSelectedRecipe(fullRecipe);
    setRecipeFormOpen(true);
    
  } catch (error) {
    console.error('❌ Error in edit recipe flow:', error);
    setError('Failed to load recipe details');
  } finally {
    setLoading(false);
  }
};

  // FIXED: Handle recipe save with proper data refresh
  const handleRecipeSave = async (recipeFormData) => {
    if (!selectedMenuItem) {
      setError(t('menu.categoryRequired'));
      return;
    }

    if (!recipeFormData.ingredients || recipeFormData.ingredients.length === 0) {
      setError(t('ingredients.recipe.noIngredients'));
      return;
    }

    if (!recipeFormData.yield_quantity || recipeFormData.yield_quantity <= 0) {
      setError(t('ingredients.validation.quantityRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Calculate total cost
      let totalCost = 0;
      recipeFormData.ingredients.forEach(ingredient => {
        const quantity = parseFloat(ingredient.quantity_per_yield || 0);
        const costPerUnit = parseFloat(ingredient.cost_per_unit || 0);
        totalCost += quantity * costPerUnit;
      });

      const yieldQuantity = parseInt(recipeFormData.yield_quantity) || 1;
      const costPerYield = totalCost / yieldQuantity;

      const recipePayload = {
        item_id: parseInt(selectedMenuItem.item_id),
        yield_quantity: parseInt(recipeFormData.yield_quantity),
        yield_unit: recipeFormData.yield_unit,
        cost_per_yield: costPerYield,
        ingredients: recipeFormData.ingredients.map(ing => ({
          ingredient_id: ing.ingredient_id,
          quantity_per_yield: parseFloat(ing.quantity_per_yield),
          unit: ing.unit
        }))
      };

      console.log('🔄 Saving recipe:', recipePayload);

      // Save recipe
      const response = await inventoryService.saveRecipe(recipePayload);
      
      if (response.success) {
        const successMessage = `Recipe ${selectedRecipe ? 'updated' : 'created'} successfully for ${selectedMenuItem?.name}`;
        setSuccessMessage(successMessage);
        
        if (onSuccess) {
          onSuccess(successMessage);
        }
        
        setRecipeFormOpen(false);
        
        // FIXED: Refresh all data after save
        await loadAllData();
        
      } else {
        throw new Error(response.message || 'Failed to save recipe');
      }
    } catch (error) {
      console.error('❌ Error saving recipe:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save recipe';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Calculate profit metrics for display with proper totalCost calculation
  const calculateProfitMetrics = (menuItem, recipe) => {
    if (!recipe) return null;

    const sellingPrice = parseFloat(menuItem.selling_price || menuItem.price || 0);
    const yieldQuantity = parseFloat(recipe.yield_quantity || 1);
    const costPerYield = parseFloat(recipe.cost_per_yield || recipe.calculated_cost || 0);
    const costPerServing = costPerYield / yieldQuantity;
    const profitPerServing = sellingPrice - costPerServing;
    const profitMarginPercentage = sellingPrice > 0 ? (profitPerServing / sellingPrice) * 100 : 0;

    // FIXED: Calculate totalCost properly
    const totalCost = costPerYield * yieldQuantity;

    return {
      sellingPrice,
      yieldQuantity,
      costPerYield,
      costPerServing,
      profitPerServing,
      profitMarginPercentage,
      totalSellingPrice: sellingPrice * yieldQuantity,
      totalProfitMargin: profitPerServing * yieldQuantity,
      totalCost // FIXED: Added missing totalCost
    };
  };

  // FIXED: Render ingredient summary with proper data
  const renderIngredientSummary = (recipe) => {
    if (!recipe?.ingredients || recipe.ingredients.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
          {t('ingredients.recipe.noIngredients')}
        </Typography>
      );
    }

    const displayCount = isMobile ? 2 : 3;
    const ingredientsToShow = recipe.ingredients.slice(0, displayCount);
    const remainingCount = Math.max(0, recipe.ingredients.length - displayCount);

    return (
      <Box>
        <Stack spacing={0.5}>
          {ingredientsToShow.map((ingredient, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.primary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                {ingredient.ingredient_name || ingredient.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
                {formatQuantity(ingredient.quantity_per_yield)} {ingredient.unit}
              </Typography>
            </Box>
          ))}
        </Stack>
        {remainingCount > 0 && (
          <Typography variant="caption" color="primary.main" sx={{ 
            fontSize: isMobile ? '0.65rem' : '0.7rem', 
            fontWeight: 600,
            mt: 0.5,
            display: 'block'
          }}>
            +{remainingCount} more ingredient{remainingCount > 1 ? 's' : ''}
          </Typography>
        )}
      </Box>
    );
  };

  // Render profit summary
  const renderProfitSummary = (menuItem, recipe) => {
    const metrics = calculateProfitMetrics(menuItem, recipe);
    if (!metrics) return null;

    return (
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Box sx={{ textAlign: 'center', p: isMobile ? 0.5 : 1, backgroundColor: theme.palette.info.lighter || 'rgba(33, 150, 243, 0.1)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
              Cost/Serving
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? '0.75rem' : '0.8rem', color: theme.palette.warning.main }}>
              ${safeFormat(metrics.costPerServing)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ textAlign: 'center', p: isMobile ? 0.5 : 1, backgroundColor: theme.palette.success.lighter || 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
              Profit/Serving
            </Typography>
            <Typography variant="body2" sx={{ 
              fontWeight: 600, 
              fontSize: isMobile ? '0.75rem' : '0.8rem',
              color: metrics.profitPerServing > 0 ? theme.palette.success.main : theme.palette.error.main 
            }}>
              ${safeFormat(metrics.profitPerServing)}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ textAlign: 'center', p: isMobile ? 0.5 : 1, backgroundColor: theme.palette.primary.lighter || 'rgba(25, 118, 210, 0.1)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
              {t('inventory.setup.totalYield')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? '0.75rem' : '0.8rem', color: theme.palette.primary.main }}>
              {formatQuantity(recipe.yield_quantity)} {recipe.yield_unit || 'servings'}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ textAlign: 'center', p: isMobile ? 0.5 : 1, backgroundColor: theme.palette.secondary.lighter || 'rgba(156, 39, 176, 0.1)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
              Margin %
            </Typography>
            <Typography variant="body2" sx={{ 
              fontWeight: 600, 
              fontSize: isMobile ? '0.75rem' : '0.8rem',
              color: metrics.profitMarginPercentage > 0 ? theme.palette.success.main : theme.palette.error.main 
            }}>
              {safeFormat(metrics.profitMarginPercentage, 1)}%
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  };

  // FIXED: Render expanded details with proper data and error handling
  const renderExpandedDetails = (menuItem, recipe) => {
    const metrics = calculateProfitMetrics(menuItem, recipe);
    if (!metrics) return null;

    return (
      <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
        {/* Complete Ingredient List */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 600, 
            mb: 1, 
            color: theme.palette.primary.main, 
            fontSize: isMobile ? '0.8rem' : '0.85rem' 
          }}>
            📋 Complete Ingredients ({recipe.ingredients?.length || 0})
          </Typography>
          <List dense sx={{ py: 0 }}>
            {recipe.ingredients?.map((ingredient, index) => (
              <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.8rem' }}>
                        {ingredient.ingredient_name || ingredient.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                        {formatQuantity(ingredient.quantity_per_yield)} {ingredient.unit}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            )) || (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('ingredients.recipe.noIngredients')}
              </Typography>
            )}
          </List>
        </Box>

        {/* Detailed Financial Analysis */}
        <Box>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 600, 
            mb: 1, 
            color: theme.palette.success.main, 
            fontSize: isMobile ? '0.8rem' : '0.85rem' 
          }}>
            💰 Financial Analysis
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6} sm={6}>
              <Box sx={{ p: isMobile ? 1 : 1.5, backgroundColor: theme.palette.warning.lighter || 'rgba(255, 152, 0, 0.1)', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
                  {t('ingredients.recipe.totalCost')} ({formatQuantity(recipe.yield_quantity)} {recipe.yield_unit})
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.warning.main, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                  ${safeFormat(metrics.totalCost)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={6}>
              <Box sx={{ p: isMobile ? 1 : 1.5, backgroundColor: theme.palette.primary.lighter || 'rgba(25, 118, 210, 0.1)', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
                  Total Revenue ({formatQuantity(recipe.yield_quantity)} {recipe.yield_unit})
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.primary.main, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                  ${safeFormat(metrics.totalSellingPrice)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ 
                p: isMobile ? 1 : 1.5, 
                backgroundColor: metrics.totalProfitMargin > 0 ? (theme.palette.success.lighter || 'rgba(76, 175, 80, 0.1)') : (theme.palette.error.lighter || 'rgba(244, 67, 54, 0.1)'), 
                borderRadius: 1, 
                textAlign: 'center' 
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
                  Total Profit Margin ({formatQuantity(recipe.yield_quantity)} {recipe.yield_unit})
                </Typography>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  color: metrics.totalProfitMargin > 0 ? theme.palette.success.main : theme.palette.error.main,
                  fontSize: isMobile ? '1.1rem' : '1.25rem'
                }}>
                  ${safeFormat(metrics.totalProfitMargin)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  // FIXED: Render menu item card with proper styling and data
  const renderMenuItemCard = (menuItem, index) => {
    const { recipe, categoryInfo, category, hasRecipe } = menuItem;
    const isExpanded = expandedCards[menuItem.item_id];
    const cardStyling = getCardStyling(category, hasRecipe);

    return (
      <Grid item xs={12} sm={6} md={viewMode === 'grid' ? (isTablet ? 6 : 4) : 12} lg={viewMode === 'grid' ? 3 : 12} key={menuItem.item_id}>
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          sx={{
            height: viewMode === 'list' ? 'auto' : '100%',
            minHeight: viewMode === 'list' ? 'auto' : (isMobile ? 380 : 420),
            borderRadius: 3,
            border: hasRecipe ? '2px solid' : '1px solid',
            borderColor: cardStyling.borderColor,
            background: cardStyling.background,
            boxShadow: cardStyling.boxShadow,
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: viewMode === 'list' ? 'row' : 'column',
            '&:hover': {
              transform: isMobile ? 'none' : 'translateY(-4px)',
              boxShadow: hasRecipe ? cardStyling.boxShadow : '0 8px 16px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          <CardContent sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            p: isMobile ? 2 : 2.5
          }}>
            {/* Header with Category */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start', 
                mb: 1,
                flexDirection: isMobile && viewMode === 'list' ? 'column' : 'row',
                gap: isMobile && viewMode === 'list' ? 1 : 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ 
                    backgroundColor: hasRecipe ? theme.palette.success.main : theme.palette.grey[400], 
                    width: isMobile ? 32 : 40, 
                    height: isMobile ? 32 : 40,
                    mr: 1.5
                  }}>
                    {hasRecipe ? <Restaurant /> : <LocalDining />}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      fontSize: isMobile ? '0.9rem' : (isTablet ? '1rem' : '1.1rem'),
                      lineHeight: 1.3,
                      mb: 0.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {menuItem.name}
                    </Typography>
                    {/* FIXED: Make menu item price more prominent */}
                    <Typography variant="h6" color="primary.main" sx={{ 
                      fontWeight: 700, 
                      fontSize: isMobile ? '1rem' : '1.2rem',
                      mb: 1
                    }}>
                      ${safeFormat(menuItem.price || menuItem.selling_price)}
                    </Typography>
                  </Box>
                </Box>
                <Stack spacing={0.5} sx={{ alignItems: isMobile && viewMode === 'list' ? 'flex-start' : 'flex-end' }}>
                  <Chip
                    icon={hasRecipe ? <CheckCircle /> : <Warning />}
                    label={hasRecipe ? t('ingredients.recipe.title') + ' Ready' : 'No Recipe'}
                    size="small"
                    color={hasRecipe ? 'success' : 'default'}
                    sx={{ fontWeight: 600, fontSize: isMobile ? '0.65rem' : '0.7rem' }}
                  />
                  <Chip
                    icon={<Category />}
                    label={categoryInfo.label}
                    size="small"
                    color={categoryInfo.color}
                    sx={{ fontWeight: 600, fontSize: isMobile ? '0.65rem' : '0.7rem' }}
                  />
                </Stack>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* Recipe Content */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              {hasRecipe ? (
                <>
                  {/* Ingredients Summary */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 600, 
                      mb: 1, 
                      color: 'text.primary',
                      fontSize: isMobile ? '0.8rem' : '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      <Inventory2 sx={{ fontSize: isMobile ? 14 : 16 }} />
                      {t('ingredients.ingredient')}s ({recipe.ingredients?.length || 0})
                    </Typography>
                    {renderIngredientSummary(recipe)}
                  </Box>

                  {/* Profit Summary */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 600, 
                      mb: 1, 
                      color: 'text.primary',
                      fontSize: isMobile ? '0.8rem' : '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      <TrendingUp sx={{ fontSize: isMobile ? 14 : 16 }} />
                      Recipe Metrics
                    </Typography>
                    {renderProfitSummary(menuItem, recipe)}
                  </Box>

                  {/* Expanded Details */}
                  <Collapse in={isExpanded}>
                    {renderExpandedDetails(menuItem, recipe)}
                  </Collapse>

                  {/* Spacer to push buttons to bottom */}
                  <Box sx={{ flexGrow: 1 }} />

                  {/* Action Buttons */}
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size={isMobile ? "medium" : "small"}
                      startIcon={<Edit />}
                      onClick={() => handleEditRecipe(menuItem, recipe)}
                      disabled={loading}
                      sx={{ 
                        borderColor: theme.palette.success.main,
                        color: theme.palette.success.main,
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        '&:hover': {
                          borderColor: theme.palette.success.dark,
                          backgroundColor: theme.palette.success.lighter || 'rgba(76, 175, 80, 0.1)'
                        }
                      }}
                    >
                      {t('common.edit')} {t('ingredients.recipe.title')}
                    </Button>
                    
                    <Button
                      variant="text"
                      size={isMobile ? "medium" : "small"}
                      endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                      onClick={() => handleCardExpand(menuItem.item_id)}
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                    >
                      {isExpanded ? 'Show Less' : 'Show Details'}
                    </Button>
                  </Stack>
                </>
              ) : (
                <>
                  {/* No Recipe State */}
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: isMobile ? 2 : 3,
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <Restaurant sx={{ fontSize: isMobile ? 40 : 48, color: 'text.secondary', mb: 1, opacity: 0.5 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: isMobile ? '0.8rem' : '0.85rem' }}>
                      {t('ingredients.recipe.noRecipe')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      lineHeight: 1.4,
                      mb: 3,
                      px: 1
                    }}>
                      Create a recipe to calculate costs, profits, and manage ingredients automatically
                    </Typography>
                  </Box>

                  {/* Action Button */}
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleCreateRecipe(menuItem)}
                    size={isMobile ? "medium" : "small"}
                    fullWidth={isMobile}
                    disabled={loading}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                      }
                    }}
                  >
                    Create Recipe
                  </Button>
                </>
              )}
            </Box>
          </CardContent>
        </MotionCard>
      </Grid>
    );
  };

  if (loading && menuItems.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
            {t('ingredients.recipe.title')} Setup
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
            Create and manage recipes for your menu items to calculate costs and profits automatically
          </Typography>
        </Box>
        <Button
          startIcon={<Refresh />}
          onClick={loadAllData}
          disabled={loading}
          variant="outlined"
          size="small"
        >
          {t('common.refresh')}
        </Button>
      </Box>

      {/* Error/Success Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {success}
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 2, p: isMobile ? 2 : 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('common.searchPlaceholder') || 'Search menu items...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: isMobile ? '0.875rem' : '1rem'
                  }
                }}
              />
            </Grid>

            {/* Category Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>{t('common.category')}</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label={t('common.category')}
                  startAdornment={<FilterList fontSize="small" sx={{ mr: 1 }} />}
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }
                  }}
                >
                  <MenuItem value="all">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{t('inventory.filters.showAll')}</span>
                      <Chip size="small" label={categoryCounts.all} />
                    </Box>
                  </MenuItem>
                  <MenuItem value="uncategorized">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{t('inventory.filters.uncategorized')}</span>
                      <Chip size="small" label={categoryCounts.uncategorized} color="default" />
                    </Box>
                  </MenuItem>
                  <MenuItem value="raw">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{t('inventory.filters.raw')}</span>
                      <Chip size="small" label={categoryCounts.raw} color="info" />
                    </Box>
                  </MenuItem>
                  <MenuItem value="premade">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{t('inventory.filters.premade')}</span>
                      <Chip size="small" label={categoryCounts.premade} color="success" />
                    </Box>
                  </MenuItem>
                  {categoryCounts.mixed > 0 && (
                    <MenuItem value="mixed">
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <span>Mixed Recipe</span>
                        <Chip size="small" label={categoryCounts.mixed} color="warning" />
                      </Box>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* View Mode Toggle */}
            {!isSmallMobile && (
              <Grid item xs={12} sm={6} md={2}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setViewMode(newMode)}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="grid" sx={{ flex: 1 }}>
                    <ViewModule fontSize="small" />
                    <Typography variant="caption" sx={{ ml: 0.5, display: { xs: 'none', sm: 'inline' } }}>
                      Grid
                    </Typography>
                  </ToggleButton>
                  <ToggleButton value="list" sx={{ flex: 1 }}>
                    <ViewList fontSize="small" />
                    <Typography variant="caption" sx={{ ml: 0.5, display: { xs: 'none', sm: 'inline' } }}>
                      List
                    </Typography>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            )}

            {/* Results Count */}
            <Grid item xs={12} md={isSmallMobile ? 12 : 3}>
              <Typography variant="body2" color="text.secondary" sx={{ 
                textAlign: { xs: 'left', md: 'right' },
                fontSize: isMobile ? '0.8rem' : '0.875rem'
              }}>
                {filteredMenuItems.length} of {menuItems.length} items
                {searchTerm && ` matching "${searchTerm}"`}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Info Alert */}
      <Alert 
        severity="info" 
        sx={{ mb: 3, borderRadius: 2 }}
        icon={<TrendingUp />}
      >
        <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
          Create recipes to automatically calculate ingredient costs, profit margins, and manage inventory efficiently. 
          Recipe categories are determined by the types of ingredients used: Raw Materials, Pre-made Items, or Mixed.
        </Typography>
      </Alert>

      {/* Menu Items Grid/List */}
      {filteredMenuItems.length > 0 ? (
        <Grid container spacing={isMobile ? 2 : 3}>
          {filteredMenuItems.map((menuItem, index) => renderMenuItemCard(menuItem, index))}
        </Grid>
      ) : searchTerm || categoryFilter !== 'all' ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
            No menu items found matching your search criteria. Try adjusting your search term or filters.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
            {t('inventory.noMenuItems') || 'No menu items found. Please add menu items first before setting up recipes.'}
          </Typography>
        </Alert>
      )}

      {/* Recipe Form Dialog */}
      <RecipeForm
        open={recipeFormOpen}
        onClose={() => {
          setRecipeFormOpen(false);
          setSelectedMenuItem(null);
          setSelectedRecipe(null);
        }}
        onSave={handleRecipeSave}
        menuItem={selectedMenuItem}
        recipe={selectedRecipe}
        ingredients={ingredients}
      />
    </Box>
  );
};

export default RecipeSetupTab;